/* eslint-disable jsx-a11y/accessible-emoji */

import {
    Box,
    Button,
    Flex,
    Grid,
    Image,
    Spacer,
    Text,
    useToast
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdEmail, MdFileDownload, MdPictureAsPdf } from "react-icons/md";
import { useParams } from "react-router-dom";
import { AccountAvatar } from "../../components/AccountAvatar";
import { Comment } from "../../components/Comment";
import { Loader } from "../../components/Loader";
import { Title } from "../../components/Title";
import { CommentForm, MedicalCardForm } from "../../forms";
import {
    GetMedicalCardDocumentDocument,
    useCreateDocumentCommentMutation,
    useDeleteDocumentCommentMutation,
    useGetMedicalCardDocumentQuery,
    useMeQuery,
    useUpdateDocumentCommentMutation
} from "../../graphql";
import { useDashboardTitle } from "../../hooks/useDashboardTitle";
import { download, formatDateTime, getFullName } from "../../utils";

export const MedicalCardDocument: React.FC = () => {
    const { t } = useTranslation();
    const { setTitle } = useDashboardTitle();
    const { visitId, documentId, clientId } = useParams<{
    visitId: string;
    clientId: string;
    documentId: string;
  }>();
    const toast = useToast();

    const { data: observer, loading: loadingMe } = useMeQuery({
        fetchPolicy: "cache-first"
    });

    const [createMedicalCardComment] = useCreateDocumentCommentMutation();
    const { data, loading } = useGetMedicalCardDocumentQuery({
        variables: {
            id: parseInt(documentId)
        }
    });
    const [updateMedicalCardComment] = useUpdateDocumentCommentMutation();
    const [deleteMedicalCardComment] = useDeleteDocumentCommentMutation();

    const medicalCardDocument = data?.getMedicalCardDocument;

    const onCommentSumbit = async (values) => {
        await createMedicalCardComment({
            variables: {
                documentId: medicalCardDocument.id,
                content: values.content
            },
            refetchQueries: [
                {
                    query: GetMedicalCardDocumentDocument,
                    variables: {
                        id: parseInt(documentId)
                    }
                }
            ]
        });

        toast({
            title: t("forms.success"),
            description: t("patients.medicalCards.detailsTab.commentAdded"),
            status: "success",
            isClosable: true
        });
    };

    const onDownload = (url) => {
        download(url);
    };

    const onCommentUpdate = async (id, content) => {
        await updateMedicalCardComment({
            variables: {
                id: id,
                content: content
            },
            refetchQueries: [
                {
                    query: GetMedicalCardDocumentDocument,
                    variables: {
                        id: parseInt(documentId)
                    }
                }
            ]
        });

        toast({
            title: t("forms.success"),
            description: t("patients.medicalCards.detailsTab.commentEdited"),
            status: "success",
            isClosable: true
        });
    };

    const onCommentDelete = async (id) => {
        await deleteMedicalCardComment({
            variables: {
                id
            },
            refetchQueries: [
                {
                    query: GetMedicalCardDocumentDocument,
                    variables: {
                        id: parseInt(documentId)
                    }
                }
            ]
        });

        toast({
            title: t("forms.success"),
            description: t("patients.medicalCards.detailsTab.commentDeleted"),
            status: "success",
            isClosable: true
        });
    };

    useEffect(() => {
        if (!loading) {
            setTitle([
                t("patients.medicalCards.detailsTab.accounts"),
                {
                    to: `/account/${medicalCardDocument?.medicalCard?.patient?.id}`,
                    text: getFullName(medicalCardDocument?.medicalCard?.patient)
                },
                t("patients.medicalCards.documentsTab.documentByName", {
                    name: medicalCardDocument.description
                })
            ]);
        }

    // eslint-disable-next-line
  }, [loading]);

    return (
        <>
            { loading || loadingMe ? (
                <Loader />
            ) : (
                <>
                    <Grid templateColumns="repeat(2, 1fr)" gap={ 6 }>
                        <Box
                            borderWidth="1px"
                            borderRadius="lg"
                            overflow="hidden"
                            background="white"
                        >
                            <Title title={ t("patients.medicalCards.documentsTab.details") }>
                                <Button
                                    rightIcon={ <MdFileDownload /> }
                                    colorScheme="blue"
                                    size="sm"
                                    variant="ghost"
                                    onClick={ () => onDownload(medicalCardDocument.media.url) }
                                    marginRight="10px"
                                    // isLoading={loadingPDF}
                                >
                                    { t("patients.medicalCards.detailsTab.download") }
                                </Button>
                            </Title>
                            <Box>
                                <Flex>
                                    <Box p="4">
                                        <AccountAvatar account={ medicalCardDocument.author } />
                                    </Box>
                                    <Spacer />
                                    <Box p="4">
                                        { formatDateTime(medicalCardDocument.createdAt) }
                                    </Box>
                                </Flex>
                            </Box>
                            <Box bg="#E9E9E9" w="100%" p={ 4 } color="#4F4F4F">
                                <small>
                                    <Text>
                                        { t("patients.medicalCards.documentsTab.description") }
                                    </Text>
                                </small>
                                <Text>{ medicalCardDocument.description }</Text>
                            </Box>
                            <Box>
                                { medicalCardDocument.media.contentType.startsWith("image") ? (
                                    <Image src={ medicalCardDocument.media.url } />
                                ) : (
                                    <Text
                                        m={ 5 }
                                        dangerouslySetInnerHTML={ {
                                            __html: t(
                                                "patients.medicalCards.documentsTab.formatNotSupported",
                                                {
                                                    format: medicalCardDocument.media.contentType
                                                }
                                            )
                                        } }
                                    ></Text>
                                ) }
                            </Box>
                        </Box>

                        <Box
                            borderWidth="1px"
                            borderRadius="sm"
                            overflow="hidden"
                            background="white"
                        >
                            <Title
                                title={ t("patients.medicalCards.detailsTab.comments") }
                            ></Title>
                            { medicalCardDocument.comments.map((comment) => (
                                <Comment
                                    key={ comment.id }
                                    comment={ comment }
                                    loggedUser={ observer }
                                    onSubmit={ onCommentUpdate }
                                    onDelete={ onCommentDelete }
                                />
                            )) }

                            <CommentForm
                                onSubmit={ onCommentSumbit }
                                initialValues={ { content: "" } }
                            ></CommentForm>
                        </Box>
                    </Grid>
                </>
            ) }
        </>
    );
};
